import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ICacheManager,
    Party,
    Policy,
    POLICY_STATUS_MTA,
    PolicyPropertyEnum,
    PremiumType,
    ProductsEnum,
    Renewal,
    Types
} from "@ferry/journey-services";
import {
    allowToRenderFutureMTAExplainedHeader,
    findLatestNotFutureMTAPolicyVersion,
    getAmountWithDecimalDigits,
} from "../../../../../CommonFunctions";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {StorageSuccessModal} from "../../../../../components/modals/StorageSuccessModal";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {AnnualYourCarPanel} from "./AnnualYourCarPanel";
import {EditCarMtaSuccessModalWordingGenerator} from "./modal/EditCarMtaSuccessModalWordingGenerator";
import {ReplaceCarMtaSuccessModalWordingGenerator} from "./modal/ReplaceCarMtaSuccessModalWordingGenerator";
import {RemoveDriverMtaSuccessModalWordingGenerator} from "./modal/RemoveDriverMtaSuccessModalWordingGenerator";
import {EditDriverMtaSuccessModalWordingGenerator} from "./modal/EditDriverMtaSuccessModalWordingGenerator";
import {AddDriverMtaSuccessModalWordingGenerator} from "./modal/AddDriverMtaSuccessModalWordingGenerator";
import {FutureMTAExplainedHeader} from "./FutureMTAExplainedHeader";
import {MtaLimitWarningBanner, FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import * as moment from "moment";
import {pushEventToAdobeDataLayerTimeout} from "../../../../../utils/AdobeDataLayerHelper";
import {MtaModalWordingParams} from "./modal/MtaModalWordingParams";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {Types as CustomerPortalTypes} from "../../../../../Types";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}


interface IWolfYourCarsPageState {

    policy?: Policy;
    policies?: Policy[];
    policyLoaded: boolean;
    party?: Party;
    partyLoaded: boolean;
    versionHistory: Policy[];
    renewalProposal?: Renewal;
    previousMonthlyPremium?: number;
    renewalEnabled: boolean;
}

@authorize
export class AnnualYourCarsPagePlain extends React.Component<IWithRouterProps<IParams>, IWolfYourCarsPageState> {
    private pluginTemplates = new PluginTemplates();

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            policies: [],
            policyLoaded: false,
            partyLoaded: false,
            versionHistory: [],
            renewalProposal: undefined,
            renewalEnabled: false
        };
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Your cars");

        // In case this page was navigated to from a successful MTA, ensure that the most recent policy version is loaded
        if (window.sessionStorage.getItem("showPopup") === "true") {
            this.cacheManager.clearCache("getPolicy");
        }

        this.customerPortalService.getPolicies().subscribe((policies: Policy[]) => this.setState({
            policies: this.getPoliciesByProduct(policies, ProductsEnum.WOLF_MOTOR)}));

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {
            policy.policyProperties.forEach(property => {
                if (property.key === PolicyPropertyEnum.PERFORM_RENEWAL) {
                    this.setState({renewalEnabled: property.value === 'true'});
                }
            });

            if (policy) {
                this.customerPortalService.getRenewalProposal(policyNumber).subscribe(
                    (renewalProposal: Renewal) => {
                        this.setState({
                            renewalProposal
                        })
                    }
                );

                this.customerPortalService.getPolicyHistory(policyNumber).subscribe((versions: Policy[]) => {
                    this.setState({
                        versionHistory: versions
                    })
                });
            }

            this.setState({
                policy,
                policyLoaded: true,
                previousMonthlyPremium: policy.annualPolicyInfo.previousMonthlyPremium
            })
        });

        this.customerPortalService.getProfile().subscribe((party: Party) => {
            this.setState({
                party,
                partyLoaded: true
            });
        });

        const successModalRef = window.sessionStorage.getItem("referrerJourney");
        if (window.sessionStorage.getItem("showPopup") === "true"
            && (successModalRef?.startsWith("mta") || successModalRef === "replaceCar")) {
            this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "mta_completed");
        }
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        if (this.state.policyLoaded && this.state.partyLoaded) {
            const policy: Policy = this.state.policy!;

            return (
                <>
                    <FalconHeader/>
                    <div className="page-contents your-car-container">
                        <FalconNavigatorHeader
                            back={{
                                link: `/dashboard/annualMotor/${this.props.params.policyNumber}`,
                            }}
                            title="Your car, make changes"/>
                        {this.state.policy && allowToRenderFutureMTAExplainedHeader(policy.interaction.process) &&
                            <FutureMTAExplainedHeader
                                policyNumber={this.props.params.policyNumber}
                                policyEndDate={policy.endDate}
                                upcomingMtaDate={policy.annualPolicyInfo.maxFutureMtaDate}
                            />
                        }
                        {this.state.policy?.interaction &&
                            <MtaLimitWarningBanner interaction={this.state.policy.interaction}/>
                        }
                        {this.renderPageContents()}
                        {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${this.props.params.policyNumber}`)}
                    </div>
                    <Footer/>
                </>
            );
        } else {
            return (<Overlay show={true}/>);
        }
    }

    private renderPageContents = () => {
        if (!this.state.policyLoaded || !this.state.partyLoaded) {
            return <Overlay show={true}/>;
        }

        const showSuccessModal = window.sessionStorage.getItem("showPopup") === "true";

        return (
            <div className="page-contents-inner">
                {showSuccessModal && this.renderSuccessModal()}
                {this.renderYourCarCoverPanels()}
            </div>
        );
    };

    private renderYourCarCoverPanels() {
        const {policy} = this.state;
        let currentlyActivePolicy: Policy | undefined;

        if (!policy) {
            return;
        }

        if (policy.status === POLICY_STATUS_MTA && moment().isBefore(policy.interaction.headerInfo.startDate)) {
            currentlyActivePolicy = findLatestNotFutureMTAPolicyVersion(this.state.versionHistory!, policy.policyNumber);
        }

        return (
            <>
                <AnnualYourCarPanel
                    key={policy.policyNumber}
                    interaction={policy.interaction}
                    currentlyActiveInteraction={currentlyActivePolicy?.interaction}
                    vehicleIndex={0}
                    annualPolicyInfo={policy.annualPolicyInfo}
                    policyEndDate={policy.endDate}
                />
                <ButtonLink id="make-claim" primary={true} to={`/dashboard/pickMotorClaimKind/${policy.policyNumber}`} className="make-claim">
                    Make a claim
                </ButtonLink>
                {this.renderCancelPolicyButton(policy)}
            </>
        );
    }

    private renderSuccessModal = (): React.ReactNode => {
        const successModalReferrer = window.sessionStorage.getItem("referrerJourney");
        switch (successModalReferrer) {
            case "mtaEditVehicleDetails":
                return this.renderMtaEditVehicleDetailsSuccessModal();
            case "mtaRemoveDriver":
                return this.renderMtaRemoveDriverSuccessModal();
            case "replaceCar":
                return this.renderMtaReplaceCarSuccessModal();
            case "mtaEditDriverDetails":
                return this.renderMtaEditDriverDetailsSuccessModal();
            case "mtaAddPermanentDriver":
                return this.renderAddAdditionalDriverSuccessModal();
            case "mtaEditVehicleRegistrationNumber":
                return this.renderEditRegistrationNumberSuccessModal();
            default:
                return null;
        }
    };

    private renderMtaEditVehicleDetailsSuccessModal = (): React.ReactNode => {

        const interaction = this.state.policy?.interaction;
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;

        const {factors, uistate, paymentDetails, policyHolder, agreeToReuseCardDetails} = interaction;

        const mtaSuccessMessageText = window.sessionStorage.getItem("mtaSuccessMessageText");

        const wording = EditCarMtaSuccessModalWordingGenerator.generateModalWording(
            factors, paymentDetails, uistate.payFullPrice, mtaSuccessMessageText,
            policyHolder.cardDetails?.at(-1)?.number, agreeToReuseCardDetails, this.state.previousMonthlyPremium, renewalPremium);

        window.sessionStorage.removeItem("mtaHeadsUpMessageText");

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={
                    <p id="mtaPaymentSuccessful">
                        {wording.modalBody}
                    </p>
                }
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={
                    <p id="mtaPaymentSuccessfulAnythingElse">
                        {wording.modalAnythingElseChangedBody}
                    </p>
                }
            />
        );
    };

    private renderMtaRemoveDriverSuccessModal = (): React.ReactNode => {
        const interaction = this.state.policy?.interaction;
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;

        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const wording = RemoveDriverMtaSuccessModalWordingGenerator.generateModalWording(factors, uistate,
            interaction.policyHolder.cardDetails?.at(-1)?.number, interaction.agreeToReuseCardDetails, this.state.previousMonthlyPremium, instalmentsNotPaidOff, renewalPremium);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={wording.modalBody}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={wording.modalAnythingElseChangedBody}
            />
        );
    };

    private renderMtaReplaceCarSuccessModal = (): React.ReactNode => {
        const interaction = this.state.policy?.interaction;
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;

        const newVehicle = interaction.vehicles[0];
        const oldVehicle = {
            registrationNumber: window.sessionStorage.getItem("oldCarVrn"),
            make: window.sessionStorage.getItem("oldCarMake"),
            model: window.sessionStorage.getItem("oldCarModel")
        };

        const {factors, uistate} = interaction;
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const wording = ReplaceCarMtaSuccessModalWordingGenerator.generateModalWording(oldVehicle, newVehicle, factors, uistate.payFullPrice,
            interaction.policyHolder.cardDetails?.at(-1)?.number, interaction.agreeToReuseCardDetails, interaction, this.state.previousMonthlyPremium, instalmentsNotPaidOff, renewalPremium);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={wording.modalBody}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={wording.modalAnythingElseChangedBody}
            />
        );
    };

    private renderMtaEditDriverDetailsSuccessModal = (): React.ReactNode => {
        const interaction = this.state.policy?.interaction;
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;
        const {factors, uistate} = interaction;

        const driverIndicator = window.sessionStorage.getItem("mtaEditDriverDetailsDriver") || "";
        const driver = driverIndicator === "policyHolder" ? interaction.policyHolder : interaction.additionalDrivers[parseInt(driverIndicator, 10)];
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;

        const modalWordingRequest : MtaModalWordingParams = {
            factors: factors,
            driver: driver,
            payFullPrice: uistate.payFullPrice,
            cardNumber: interaction.policyHolder.cardDetails?.at(-1)?.number,
            agreeToReuseCardDetails: interaction.agreeToReuseCardDetails,
            previousMonthlyPremium: this.state.previousMonthlyPremium,
            instalmentsNotPaidOff: instalmentsNotPaidOff,
            renewalPremium: renewalPremium
        };

        const wording = EditDriverMtaSuccessModalWordingGenerator.generateModalWording(modalWordingRequest);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={wording.modalBody}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={wording.modalAnythingElseChangedBody}
            />
        );
    };

    private renderAddAdditionalDriverSuccessModal = (): React.ReactNode => {
        const interaction = this.state.policy?.interaction;
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;
        const {factors, uistate} = interaction;

        const newAdditionalDriversCount: number = interaction.additionalDrivers.length - parseInt(window.sessionStorage.getItem("initialAdditionalDriversNumber")!, 10);
        const additionalDrivers: any = interaction.additionalDrivers.slice(-newAdditionalDriversCount);

        const car: any = interaction.vehicles[0];
        const instalmentsNotPaidOff: boolean = interaction.factors.premiumType === PremiumType.monthly && interaction.paymentDetails.instalmentPlan.totalAmount > 0;
        const conditions = {
            agreeToReuseCardDetails : interaction.agreeToReuseCardDetails,
            instalmentsNotPaidOff
        }
        const wording = AddDriverMtaSuccessModalWordingGenerator.generateModalWording(factors, uistate, car, additionalDrivers,
            interaction.policyHolder.cardDetails?.at(-1)?.number, conditions, this.state.previousMonthlyPremium, renewalPremium);

        return (
            <StorageSuccessModal
                title={wording.modalTitle}
                explanationWording={wording.modalBody}
                anythingElseTitle={wording.modalAnythingElseChangedTitle}
                explanationAnythingElseWording={wording.modalAnythingElseChangedBody}
            />
        );
    };

    private renderEditRegistrationNumberSuccessModal = (): React.ReactNode => {
        const modalTitle = "Change was successful";

        const interaction = this.state.policy?.interaction;
        const vehicle = interaction.vehicles[0];
        const renewalPremium = this.state.renewalProposal?.factors?.annualPremium;

        const modalBody = (
            <div>
                <p className="font-weight-extra-light">
                    You have changed the registration number of this{" "}
                    {vehicle.make}, {vehicle.model}, {vehicle.year}{" "}
                    to <span className="font-weight-bold">{vehicle.registrationNumber}</span>
                </p>
                {renewalPremium &&
                    <div className="font-weight-semi-bold mb-0">
                        <p>
                            Your renewal price has also stayed the same at {getAmountWithDecimalDigits(renewalPremium)}&nbsp;
                        </p>
                        <p>
                            Please check your renewal tile for further details
                        </p>
                    </div>
                }
            </div>
        );

        return (
            <StorageSuccessModal
                title={modalTitle}
                explanationWording={modalBody}
                anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}
            />
        );
    };

    private renderCancelPolicyButton(policy: Policy): React.ReactNode {
        if (this.state.renewalProposal && this.state.renewalEnabled) {
            return (
                <ButtonLink
                    key={policy.policyNumber}
                    id={`cancel-policy${policy.policyNumber}`}
                    className="cancel-policy"
                    primary={false}
                    to={'/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + this.state.renewalProposal?._id}
                    disabled={false}>
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink
                    key={policy.policyNumber}
                    id={`cancel-policy${policy.policyNumber}`}
                    className="cancel-policy"
                    primary={false}
                    to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${policy.interaction?._id}`}
                    disabled={false}>
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private getPoliciesByProduct(policies: Policy[], product: string): Policy[] {
        return policies?.filter((policies) => policies?.products?.includes(product));
    }
}

export const AnnualYourCarsPage = withRouter<{}, IParams>(AnnualYourCarsPagePlain)
